import React, { useState } from 'react';
import { Table, Button, Input, Tag } from 'antd';
import { SearchOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { customBaseUrl, httpGet } from '../../store/http';
import { showLoader, hideLoader } from '../../utils/loader';
import Swal from 'sweetalert2';
import PageHeadingBanner from '../../components/shared/PageHeadingBanner';
import FullResultModal from '../../components/shared/FullResultModal';

const KYCList = ({ userType = 'PERSONAL' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchKYC = async (page = 0, size = 10, isSearch = false) => {
    setLoading(true);
    showLoader();
    try {
      let url = `${customBaseUrl.auditUrl}/admin/customers/kyc?pageNo=${page}&pageSize=${size}`;
      
      if (isSearch && searchText) {
        url = `${customBaseUrl.auditUrl}/admin/customers/kyc/${searchText}`;
      }

      const response = await httpGet(url);
      
      let dataArr = [];
      let totalCount = 0;

      const rawData = response?.data || response;

      if (isSearch && searchText) {
        if (rawData && !Array.isArray(rawData)) {
          dataArr = [rawData];
          totalCount = 1;
        } else if (Array.isArray(rawData)) {
          dataArr = rawData;
          totalCount = response?.total || dataArr.length;
        }
      } else {
        dataArr = response?.data || [];
        totalCount = response?.total || dataArr.length;
      }

      setData(dataArr);
      setPagination({ 
        ...pagination, 
        current: (response?.pageNo !== undefined ? response.pageNo : page) + 1, 
        pageSize: size, 
        total: totalCount 
      });
      setHasSearched(true);
    } catch (e) {
      console.error('Error fetching KYC:', e);
      Swal.fire('Error', 'Unable to fetch KYC records.', 'error');
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchKYC(0, pagination.pageSize, !!searchText);
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      render: (id) => <span style={{ fontWeight: 600 }}>{id}</span>,
    },
    {
      title: 'Full Name',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (name, record) => name || `${record.firstName || ''} ${record.lastName || ''}` || '—',
    },
    {
      title: 'KYC Level',
      dataIndex: 'kycLevel',
      key: 'kycLevel',
      render: (level) => <Tag color="blue">{level || 'LEVEL 1'}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'VERIFIED' ? 'green' : 'orange'}>
          {status || 'PENDING'}
        </Tag>
      ),
    },
    {
      title: 'Update Date',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date) => date ? new Date(date).toLocaleDateString() : '—',
    },
  ];

  return (
    <div className="kyc-list-wrapper">
      <PageHeadingBanner
        icon={<SafetyCertificateOutlined />}
        title="KYC Compliance Reports"
        subtitle="Monitor and verify customer identity documentation"
      />

      <div className="customer-search-bar" style={{ display: 'flex', gap: '12px', margin: '20px 0' }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder="Search by User ID..."
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={handleSearch}
          style={{ flex: 1, height: '42px' }}
        />
        <Button 
          type="primary" 
          onClick={handleSearch}
          style={{ height: '42px', background: '#ff6700', borderColor: '#ff6700', borderRadius: '8px' }}
        >
          Search
        </Button>
      </div>

      <div className="customer-table-card">
        {hasSearched ? (
          <Table
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.id || record.userId}
            loading={loading}
            pagination={pagination}
            onChange={(pag) => fetchKYC(pag.current - 1, pag.pageSize, !!searchText)}
            onRow={(record) => ({
              onClick: () => {
                setSelectedRecord(record);
                setIsModalOpen(true);
              },
              style: { cursor: 'pointer' }
            })}
            className="customer-table"
          />
        ) : (
          <div style={{ padding: '80px 0', textAlign: 'center', background: '#fff', borderRadius: '12px' }}>
              <SafetyCertificateOutlined style={{ fontSize: 48, color: '#f0f0f0', marginBottom: 16 }} />
              <h3 style={{ color: '#8c8c8c' }}>KYC Record Lookup</h3>
              <p style={{ color: '#bfbfbf' }}>Enter a User ID to pull compliance and verification data</p>
          </div>
        )}
      </div>

      <FullResultModal 
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        data={selectedRecord}
        title="KYC Verification Details"
      />
    </div>
  );
};

export default KYCList;
